import styled from '@emotion/styled';
import { ChevronLeft, Headphones, ChevronRight } from 'lucide-react';
import type { UserProfile } from '@/lib/services/auth.service';
import { SubscriptionsDashboard } from './SubscriptionsDashboard';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.33;
  color: #292929;
  margin: 0;
`;

const HelpContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px 0 32px;
`;

const HelpTextBlock = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const HelpTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const HelpDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #5a5a5a;
  margin: 0;
  max-width: 600px;
`;

const SupportCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
  cursor: pointer;
  text-align: left;
  max-width: 600px;
  width: 100%;
  align-self: center;

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  }
`;

const SupportCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SupportIconCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #fcd9d1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #292929;
`;

const SupportLabel = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.56;
  color: #292929;
`;

const SupportChevron = styled.span`
  color: #767676;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

interface MembershipDashboardProps {
  readonly user: UserProfile;
  readonly onBack: () => void;
}

export function MembershipDashboard({ user, onBack }: MembershipDashboardProps) {
  return (
    <SubscriptionsDashboard
      user={user}
      planType="membership"
      activeCardLayout="list"
      header={
        <Header>
          <BackButton onClick={onBack} aria-label="Back to My Account">
            <ChevronLeft size={22} />
          </BackButton>
          <PageTitle>Manage Memberships</PageTitle>
        </Header>
      }
      emptyState={{
        title: 'No memberships yet',
        description:
          'When you subscribe to a membership plan, your memberships will appear here.',
      }}
      toasts={{
        canceled: 'Your membership has been canceled.',
      }}
      cancelFeedbackBanner={{
        heading: 'Your membership has been canceled.',
      }}
      retentionSuccessBanner={{
        productLabel: 'membership',
      }}
      footer={
        <HelpContainer>
          <HelpTextBlock>
            <HelpTitle>Have any questions?</HelpTitle>
            <HelpDescription>
              Do you need more information about products, a recent order or have questions about
              your membership?
            </HelpDescription>
          </HelpTextBlock>
          <SupportCard
            type="button"
            onClick={() => {
              /* future: navigate to support */
            }}
          >
            <SupportCardLeft>
              <SupportIconCircle>
                <Headphones size={20} />
              </SupportIconCircle>
              <SupportLabel>Priority Support</SupportLabel>
            </SupportCardLeft>
            <SupportChevron>
              <ChevronRight size={18} />
            </SupportChevron>
          </SupportCard>
        </HelpContainer>
      }
    />
  );
}
